import { useState, useEffect, useCallback, useRef } from "react";

interface UseLiveDataOptions<T> {
  /** Async function to fetch data */
  fetcher: () => Promise<T>;
  /** Polling interval in ms (default: 10000 = 10s) */
  interval?: number;
  /** Whether to start polling immediately (default: true) */
  enabled?: boolean;
}

interface UseLiveDataResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Manual refresh — triggers immediate fetch */
  refresh: () => Promise<void>;
  /** Timestamp of last successful fetch */
  lastUpdated: Date | null;
  /** Whether currently polling */
  isLive: boolean;
}

/**
 * Hook for live-updating data with auto-polling.
 * Provides loading states, error handling, and manual refresh.
 */
export function useLiveData<T>(options: UseLiveDataOptions<T>): UseLiveDataResult<T> {
  const { fetcher, interval = 10000, enabled = true } = options;
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fetcherRef = useRef(fetcher);

  // Keep fetcher ref up to date
  fetcherRef.current = fetcher;

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetcherRef.current();
      setData(result);
      setLastUpdated(new Date());
    } catch (err: any) {
      setError(err.message || "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch + polling
  useEffect(() => {
    if (!enabled) return;

    refresh();

    intervalRef.current = setInterval(refresh, interval);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [refresh, interval, enabled]);

  return {
    data,
    loading,
    error,
    refresh,
    lastUpdated,
    isLive: enabled,
  };
}

/**
 * Hook that triggers a refresh after an action completes.
 * Wraps an async action and calls refresh on success.
 */
export function useActionWithRefresh(refresh: () => Promise<void>) {
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const execute = useCallback(
    async (key: string, fn: () => Promise<any>) => {
      setBusy(key);
      setActionError(null);
      try {
        await fn();
        // Immediate refresh after action
        await refresh();
      } catch (err: any) {
        setActionError(err.message || "Action failed");
      } finally {
        setBusy(null);
      }
    },
    [refresh]
  );

  return { busy, actionError, execute, clearError: () => setActionError(null) };
}
